import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Button, Input, Layout, Menu, notification, PageHeader, Popconfirm, Row, Space, Table, Tag, Tooltip } from "antd";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

const collectionSeasonsQuery = gql`
  query ($pagination: ConnectionPagination, $searchQuery: String) {
    collectionSeasons(pagination: $pagination, searchQuery: $searchQuery) {
      nodes {
        id
        code
        nameIt
        nameEn
        kindIt
        kindEn
        isEnabled
        isStock
        shippingWindow
        shippingExpected
        brand {
          id
          code
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const deleteCollectionSeasonMutation = gql`
  mutation ($id: ID!) {
    deleteCollectionSeason(id: $id) {
      season {
        id
        code
      }
    }
  }
`;

export const List: React.FC<{}> = () => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const navigate = useNavigate();

  const variables = {
    pagination: { page: 1, pageSize: 0 },
    orderBy: { direction: "ASC", field: "CODE" },
    searchQuery,
  };
  const { loading, data, refetch } = useQuery(collectionSeasonsQuery, { variables, fetchPolicy: "cache-and-network" });

  const handleSearch = (value: string) => setSearchQuery(value);
  const handleSearchCancel = () => setSearchQuery("");
  const handleTableChange = () => {};

  const [execDelete] = useMutation(deleteCollectionSeasonMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      notification.error({
        placement: "bottomRight",
        message: "Cannot delete this Season",
      });
    },
    onCompleted: ({ deleteCollectionSeason: { season } }) => {
      notification.success({
        placement: "bottomRight",
        message: `Season "${season.code}" deleted`,
      });
      refetch();
    },
  });

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      render: (value: string, resource: any) => <NavLink to={resource.id}>{value}</NavLink>,
      sorter: (a: any, b: any) => a.code.localeCompare(b.code),
    },
    {
      title: "Name",
      dataIndex: "nameIt",
      render: (value: string, resource: any) => <NavLink to={resource.id}>{value}</NavLink>,
      sorter: (a: any, b: any) => a.nameIt.localeCompare(b.nameIt),
    },
    {
      title: "Kind",
      dataIndex: "kindIt",
      sorter: (a: any, b: any) => a.kindIt.localeCompare(b.kindIt),
      filters: [
        {
          text: 'Uomo',
          value: 'Uomo',
        },
        {
          text: 'Donna',
          value: 'Donna',
        },
        {
          text: 'Unisex',
          value: 'Unisex',
        },
        {
          text: 'Accessori',
          value: 'Accessori',
        },
      ],
      onFilter: (value: string, record: any) => record.kindIt.indexOf(value) === 0,
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "userBrands.brand",
      render: (value: string, resource: any) =>
        resource.brand.code === "palto" ? (
          <Tag>{resource.brand.code.toUpperCase()}</Tag>
        ) : (
          <Tag color="blue">{resource.brand.code.toUpperCase()}</Tag>
        ),
        sorter: (a: any, b: any) => a.brand.code.localeCompare(b.brand.code),
        filters: [
          {
            text: 'Paltò',
            value: 'palto',
          },
          {
            text: 'Replumè',
            value: 'replume',
          },
        ],
        onFilter: (value: string, record: any) => record.brand.code.indexOf(value) === 0,
    },
    {
      title: "Enabled",
      dataIndex: "isEnabled",
      render: (value: boolean) => {
        return value ? <Tag color="green">YES</Tag> : <Tag color="volcano">NO</Tag>;
      },
      sorter: (a: any, b: any) => a.isEnabled - b.isEnabled,
      filters: [
        {
          text: 'ENABLED',
          value: true,
        },
        {
          text: 'DISABLED',
          value: false,
        },
      ],
      onFilter: (value: boolean, record: any) => record.isEnabled.toString().indexOf(value) === 0,
    },
    {
      title: "Kind",
      dataIndex: "isStock",
      render: (value: boolean) => {
        return value ? "Stock" : "Seasonal";
      },
      sorter: (a: any, b: any) => a.isStock - b.isStock,
      filters: [
        {
          text: 'STOCK',
          value: true,
        },
        {
          text: 'SEASONAL',
          value: false,
        },
      ],
      onFilter: (value: boolean, record: any) => record.isStock.toString().indexOf(value) === 0,
    },
    {
      width: "80px",
      title: "Actions",
      dataIndex: "id",
      render: (id: string, resource: any) => (
        <Row justify="end">
          <Space>
            <Tooltip title="Edit" placement="bottom">
              <Button onClick={() => navigate(`${id}/edit`)} icon={<EditOutlined />}></Button>
            </Tooltip>
            <Popconfirm title="Are you sure?" icon={null} onConfirm={() => execDelete({ variables: { id } })}>
              <Tooltip title="Delete" placement="bottom">
                <Button danger icon={<DeleteOutlined />}></Button>
              </Tooltip>
            </Popconfirm>
          </Space>
        </Row>
      ),
    },
  ];

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Input.Search
              placeholder="search"
              onSearch={handleSearch}
              key={variables.searchQuery ?? "__search__"}
              defaultValue={variables.searchQuery ?? ""}
              style={{ width: 500, margin: "16px 30px" }}
            />
            <Button disabled={searchQuery.length === 0} onClick={handleSearchCancel} style={{ margin: "16px 0" }}>
              Cancel
            </Button>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <PageHeader
          ghost={true}
          title="Seasons"
          extra={
            <Button onClick={() => navigate("new")} type="primary">
              <PlusOutlined />
              New
            </Button>
          }
        />
        <Layout.Content className="content">
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data?.collectionSeasons.nodes}
            pagination={{
              hideOnSinglePage: true,
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${range} of ${total}`,
              total: data?.totalCount ?? 0,
              defaultPageSize: 20,
            }}
            onChange={handleTableChange}
          />
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
